import { useState, useRef, useCallback } from 'react';
import { LEVEL_SYSTEM } from '../appConstants';
import { SK_XP, SK_XP_REWARDED } from '../constants/storageKeys';
import { API_BASE } from '../appUtils';

const XP_FLASH_DURATION_MS = 2800;
const LEVEL_UP_DELAY_MS = 50;

export interface LevelInfo {
  level: number;
  name: string;
  xpInLevel: number;
  xpForLevel: number;
  pct: number;
  toNext: number;
  nextLevelName: string;
}

export interface XpFlash {
  amount: number;
  visible: boolean;
  key: number;
}

export interface XpLevelUpCelebration {
  visible: boolean;
  level: number;
  name: string;
}

function computeLevel(xp: number): LevelInfo {
  const lvl = LEVEL_SYSTEM.findLast(l => xp >= l.minXP) ?? LEVEL_SYSTEM[0];
  const xpInLevel = xp - lvl.minXP;
  const xpForLevel = lvl.maxXP === Infinity ? 1000 : lvl.maxXP - lvl.minXP;
  const pct = Math.min(100, Math.round((xpInLevel / xpForLevel) * 100));
  const toNext = lvl.maxXP === Infinity ? 0 : lvl.maxXP - xp;
  const nextIdx = LEVEL_SYSTEM.findIndex(l => l.level === lvl.level) + 1;
  const nextLevel = LEVEL_SYSTEM[nextIdx];
  return { level: lvl.level, name: lvl.name, xpInLevel, xpForLevel, pct, toNext, nextLevelName: nextLevel?.name ?? 'Legend' };
}

export { computeLevel };

export function useXP(authToken: string | null) {
  const [rawTotalXP, setTotalXP] = useState<number>(() => {
    try {
      const v = parseInt(localStorage.getItem(SK_XP) ?? '', 10);
      return isNaN(v) ? 0 : v;
    } catch { return 0; }
  });

  const [xpFlash, setXpFlash] = useState<XpFlash>({ amount: 0, visible: false, key: 0 });
  const [xpLevelUpCelebration, setXpLevelUpCelebration] = useState<XpLevelUpCelebration | null>(null);

  const rewardedEventsRef = useRef<Set<string>>(
    (() => {
      try { return new Set<string>(JSON.parse(localStorage.getItem(SK_XP_REWARDED) || '[]')); }
      catch { return new Set<string>(); }
    })()
  );
  const xpFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const earnXP = useCallback((amount: number, eventKey?: string) => {
    setTotalXP(prev => {
      const next = prev + amount;
      try { localStorage.setItem(SK_XP, String(next)); } catch { /* localStorage may be unavailable */ }
      const prevLevel = computeLevel(prev).level;
      const nextLevel = computeLevel(next).level;
      if (nextLevel > prevLevel) {
        const info = computeLevel(next);
        setTimeout(() => setXpLevelUpCelebration({ visible: true, level: info.level, name: info.name }), LEVEL_UP_DELAY_MS);
      }
      return next;
    });
    setXpFlash(f => ({ amount, visible: true, key: f.key + 1 }));
    if (xpFlashTimerRef.current !== null) clearTimeout(xpFlashTimerRef.current);
    xpFlashTimerRef.current = setTimeout(() => {
      setXpFlash(f => ({ ...f, visible: false }));
      xpFlashTimerRef.current = null;
    }, XP_FLASH_DURATION_MS);

    // Persist to API
    if (authToken) {
      fetch(`${API_BASE}/xp/earn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ amount, eventKey }),
      }).catch(() => { /* XP persist — best effort */ });
    }
  }, [authToken]);

  const earnXPOnce = useCallback((eventKey: string, amount: number) => {
    if (rewardedEventsRef.current.has(eventKey)) return;
    rewardedEventsRef.current.add(eventKey);
    try {
      localStorage.setItem(SK_XP_REWARDED, JSON.stringify([...rewardedEventsRef.current]));
    } catch { /* localStorage may be unavailable */ }
    earnXP(amount, eventKey);
  }, [earnXP]);

  return {
    rawTotalXP,
    setTotalXP,
    xpFlash,
    setXpFlash,
    xpLevelUpCelebration,
    setXpLevelUpCelebration,
    rewardedEventsRef,
    earnXP,
    earnXPOnce,
    computeLevel,
  };
}
